import { Box } from "@mantine/core";
import type { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useEffect, useState } from "react";
import { DateInput } from "../../../../mantine/dates/DateInput/DateInput";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { getRowId } from "../hooks/usePeriodBasedTable";
import { TableCellErrorText } from "../PeriodBasedTable";
import type { ExternalErrors, PeriodBasedRow } from "../PeriodBasedTable.types";

/** The next row starts the day after, so it has to keep at least one day. */
export function getLastDateMaxDate(
  nextRow: PeriodBasedRow | null | undefined
): DateTime | undefined {
  if (!nextRow?.last_date) {
    return undefined;
  }
  return backendDateOrDateTimeToLuxonDateTime(nextRow.last_date).minus({
    days: 1
  });
}

interface LastDateColumnConfig {
  rows: PeriodBasedRow[];
  originalRows: PeriodBasedRow[];
  externalErrors?: ExternalErrors;
  onUpdateNextRowFirstDate?: (
    nextRowId: string,
    newFirstDate: string,
    isTemporaryDate?: boolean
  ) => void;
}

export function getLastDateColumn({
  rows,
  originalRows,
  externalErrors,
  onUpdateNextRowFirstDate
}: LastDateColumnConfig): MRT_ColumnDef<PeriodBasedRow> {
  return {
    header: "Bis",
    accessorKey: "last_date",
    size: 120,
    enableSorting: false,
    Edit: ({ cell, row, table }) => {
      const isCreatingRow = table.getState().creatingRow?.id === row.id;
      const isEditingRow = table.getState().editingRow?.id === row.id;
      const isTemporaryDate = isCreatingRow || isEditingRow;

      const cachedValue = row._valuesCache.last_date as
        string | null | undefined;
      const cellValue =
        cell.getValue() === "-" ? null : (cell.getValue() as string | null);

      const initialValue = cachedValue !== undefined ? cachedValue : cellValue;
      const [value, setValue] = useState<DateTime | null>(
        initialValue === "-"
          ? null
          : backendDateOrDateTimeToLuxonDateTimeOrNull(initialValue as string)
      );

      useEffect(() => {
        row._valuesCache.last_date = value
          ? luxonDateTimeToBackendDateOrDateTime(value, "ISO 8601")
          : null;
      }, [value, row]);

      const nextRow = isEditingRow ? rows[row.index + 1] : null;

      function getMinDate() {
        const firstDate =
          row._valuesCache.first_date || row.original.first_date;
        if (firstDate) {
          return backendDateOrDateTimeToLuxonDateTime(firstDate);
        }
        return undefined;
      }

      const handleChange = (newValue: DateTime | null) => {
        setValue(newValue);

        if (newValue && onUpdateNextRowFirstDate) {
          if (newValue) {
            const newFirstDate = luxonDateTimeToBackendDateOrDateTime(
              newValue.plus({ days: 1 })
            );

            if (nextRow) {
              const originalNextRow = isEditingRow
                ? originalRows[row.index + 1]
                : null;
              const nextRowId = originalNextRow
                ? getRowId(originalNextRow)
                : getRowId(nextRow);

              onUpdateNextRowFirstDate(
                nextRowId,
                newFirstDate,
                isTemporaryDate
              );

              const editingRow = table.getState().editingRow;
              if (editingRow) {
                const editingRowData = rows[editingRow.index];
                if (editingRowData && editingRowData === nextRow) {
                  editingRow._valuesCache.first_date = newFirstDate;
                }
              }
            }
          }
        }

        const { editingRow, creatingRow } = table.getState();
        if (creatingRow?.id === row.id) {
          table.setCreatingRow(row);
        } else if (editingRow?.id === row.id) {
          table.setEditingRow(row);
        }
      };

      const rowId = getRowId(row.original);
      const externalError =
        !isCreatingRow && !isEditingRow && externalErrors?.[rowId]?.last_date;
      const errorText = externalError || undefined;

      return (
        <DateInput
          aria-label="Bis"
          error={errorText}
          maxDate={getLastDateMaxDate(nextRow)}
          minDate={getMinDate()}
          placeholder="Aktuell"
          value={value}
          onChange={handleChange}
        />
      );
    },
    Cell: ({ row }) => {
      const rowId = getRowId(row.original);
      const externalError = externalErrors?.[rowId]?.last_date;
      return (
        <Box>
          {backendDateOrDateTimeToLuxonDateTimeOrNull(
            row.original.last_date
          )?.toLocaleString({
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          }) || "Aktuell"}
          <TableCellErrorText error={externalError || undefined} />
        </Box>
      );
    }
  };
}
